import { InputComponentProps } from '@/components/input'
import { useInput, usePhoneInput } from '@/components/input-v2'
import { usePrice } from '@/components/price'
import { useAppContext } from '@/pages/_app'
import { getVenueScheduleStatus, useVenueContext } from '@/services/venue'
import { localStorage, stubStorage } from '@/utils/env'
import { venueGetOrderingTypes } from 'shared/venue'
import formatRelative from 'date-fns/formatRelative'
import { BasketOrderPrices } from '@/services/basket'
import { keys } from 'shared/utils/array'
import isEqual from 'lodash/isEqual'
import { objectFilterTruly, truly } from 'shared/utils/types'
import { makeEmmiterFor, useEffect$, useSubject, useSubscription } from '@/utils/event'
import {
  OrderLogisticsOnSite,
  OrderLogisticsTakeaway,
  PaymentMethod,
  VenueOrderingPaymentMethods,
} from 'shared/types/api'
import { sendOrder, useOrderFlatware } from '@/services/order'
import { filter, mapTo, merge, scan, share, switchMap, withLatestFrom } from 'rxjs'
import { nanoid } from 'nanoid'
import { useExcludedElements } from '@/services/excluded-elements'
import { augmentProductsByPreferences, useSelectedPreferences } from '@/services/preferences'
import router from 'next/router'
import { useObservable } from 'rxjs-hooks'
import flow from 'lodash/flow'
import { useForm } from '@/components/form'
import { checkPersonalDataConsent } from '@/components/personal-data-consent'

export const CHECKOUT_FORM_ID = 'checkoutForm'

export type CheckoutOrderState =
  | CheckoutOrderStatePending
  | CheckoutOrderStateLoading
  | CheckoutOrderStateSelectingPaymentMethod
  | CheckoutOrderStateFailed

export type CheckoutOrderStatePending = {
  type: 'pending'
}

export type CheckoutOrderStateLoading = {
  type: 'loading'
}

export type CheckoutOrderStateSelectingPaymentMethod = {
  type: 'selectingPaymentMethod'
}

export type CheckoutOrderStateFailed = {
  type: 'failed'
}

export type CheckoutProductListMode =
  | {
      type: 'normal'
    }
  | {
      type: 'delete'
    }

export type CheckoutActionState =
  | {
      type: 'submit'
      onSubmit: () => void
      text: string
    }
  | {
      type: 'onClick'
      text: string
      onClick: () => void
    }
  | {
      type: 'error'
      text: string
    }
  | {
      type: 'loading'
    }
  | {
      type: 'disabled'
      text: string
    }

export type CheckoutViewController = {
  productListMode: CheckoutProductListMode
  orderState: CheckoutOrderState
  paymentMethodsFiltered: VenueOrderingPaymentMethods
  inputs: {
    tableNumber: InputComponentProps
    phone: InputComponentProps
    onSiteComment: InputComponentProps
    takeawayComment: InputComponentProps
    name: InputComponentProps
    email: InputComponentProps
  }
  actionState: CheckoutActionState
  actions: {
    closePaymentMethodSelector: () => void
    selectPaymentMethod: (paymentMethod: PaymentMethod) => void
    toggleDeleteMode: () => void
  }
}

export function useCheckout(): CheckoutViewController {
  const { t, tracker } = useAppContext()
  const { venue, admin, orderManager, locale, availablePreferences, billing } = useVenueContext()!

  const useTableNumber = venue.features.orderingTypes?.onSite?.useTableNumber
  const venueSchedule = getVenueScheduleStatus(venue, orderManager.orderMode.value)

  const price = usePrice()
  const { basket, orderHistory } = orderManager
  const orderingTypes = venueGetOrderingTypes(venue.features)
  const currentOrderMode = orderManager.orderMode

  const paymentMethods = orderingTypes[currentOrderMode.value]?.paymentMethods || {}
  const paymentMethodsFiltered = objectFilterTruly({
    ...paymentMethods,
    online: paymentMethods.online && venue.paymentGateway ? paymentMethods.online : undefined,
  })

  const selectedPreferences = useSelectedPreferences()
  const excludedElements = useExcludedElements(venue.name)
  const flatware = useOrderFlatware()

  const orderedProducts = basket.products
    .getAll()
    .map((basketProduct) => {
      if (basketProduct.quantity === 0) {
        return null
      }

      const productGroup = venue.productGroups.find((pg) => {
        return pg.name === basketProduct.productGroupName
      })
      const product = productGroup?.products.find((p) => {
        return p.name === basketProduct.productName
      })

      if (!product) {
        return null
      }

      const productWithFitDescription = augmentProductsByPreferences(
        [product],
        availablePreferences,
        selectedPreferences,
      )[0]!

      return {
        product: productWithFitDescription,
        fitDescription: productWithFitDescription.fitDescription,
        quantity: basketProduct.quantity,
        appliedModifiers: basketProduct.modifiers,
        basketProductId: basketProduct.__id,
      }
    })
    .filter(truly)

  const events = {
    paymentMethodSelector: {
      open$: useSubject<void>(),
      close$: useSubject<void>(),
    },
    sendOrder$: useSubject<PaymentMethod>(),
    toggleDeleteMode$: useSubject<void>(),
  }

  const sendOrderDeps = useEffect$([
    currentOrderMode,
    {
      venueName: venue.name,
      selectedPreferences,
      excludedElements,
      orderedProducts,
      orderHistory,
      tracker,
      orderingTypes,
      flatware,
    },
  ])

  const sendOrderResponse$ = events.sendOrder$.pipe(
    withLatestFrom(sendOrderDeps),
    switchMap(([paymentMethod, [currentOrderMode, sendOrderArgs]]) => {
      const clientOrderUuid = nanoid(10)
      const logistics = ((): OrderLogisticsOnSite | OrderLogisticsTakeaway => {
        switch (currentOrderMode.value) {
          case 'onSite':
            return {
              type: 'onSite',
              contacts: {
                phone: inputs.phone.value.current ?? undefined, // TODO implicit?
                email: inputs.email.value.current ?? undefined,
                tableNumber: inputs.tableNumber.value.current ?? undefined,
                comment: inputs.onSiteComment.value.current ?? undefined,
              },
            }
          case 'takeaway':
            return {
              type: 'takeaway',
              contacts: {
                phone: inputs.phone.value.current!, // TODO implicit?
                email: inputs.email.value.current ?? undefined,
                name: inputs.name.value.current!,
                comment: inputs.takeawayComment.value.current!,
              },
            }
          default:
            throw new Error(`unexpected orderMode: ${currentOrderMode.value}`)
        }
      })()

      return sendOrder(orderManager, {
        ...sendOrderArgs,
        clientUuid: clientOrderUuid,
        paymentMethod: paymentMethod.type,
        logistics,
      })
    }),
    share(),
  )

  useSubscription(sendOrderResponse$, (sendingResult) => {
    const goToOrderPage = () => {
      return router.push(`/venue/${venue.name}/order/latest`).then(() => {
        return window.scrollTo(0, 0)
      })
    }

    switch (sendingResult.type) {
      case 'numberReady':
        return goToOrderPage().then(() => {
          return basket.products.clear()
        })
      case 'paymentPending':
        // eslint-disable-next-line immutable/no-mutation
        location.href = sendingResult.formUrl!
        return
      case 'serverStatusError':
        return goToOrderPage()
      case 'createOrderHasFailed':
        return
      default:
        return goToOrderPage()
    }
  })

  const defaultProductListMode = { type: 'normal' } as CheckoutProductListMode
  const productListMode$ = events.toggleDeleteMode$.pipe(
    scan((prev) => {
      console.log('prev', prev)
      return prev.type === 'normal' ? ({ type: 'delete' } as const) : ({ type: 'normal' } as const)
    }, defaultProductListMode),
  )
  const productListMode = useObservable<CheckoutProductListMode>(() => {
    return productListMode$
  }, defaultProductListMode)

  const orderState$ = merge(
    events.sendOrder$.pipe(mapTo({ type: 'loading' } as const)),
    sendOrderResponse$.pipe(
      filter((sendingResult) => {
        return sendingResult.type === 'createOrderHasFailed'
      }),
      mapTo({ type: 'failed' } as const),
    ),
    events.paymentMethodSelector.open$.pipe(mapTo({ type: 'selectingPaymentMethod' } as const)),
    events.paymentMethodSelector.close$.pipe(mapTo({ type: 'pending' } as const)),
  )

  const orderState = useObservable<CheckoutOrderState>(
    () => {
      return orderState$
    },
    { type: 'pending' } as CheckoutOrderState,
  )

  const actionState = flow(
    (): CheckoutActionState => {
      if (basket.prices === 'loading') {
        return {
          type: 'loading',
        }
      }

      // copy paste PAYMENT_IS_OVERDUE
      const paymentIsOverdue = (() => {
        switch (billing.subscription.status.type) {
          case 'setup':
            return false
          case 'trial':
          case 'active':
            return billing.subscription.status.paymentStatus.type === 'overdue'
        }
      })()

      if (paymentIsOverdue) {
        return {
          type: 'disabled',
          text: t('checkout:weAreClosed'),
        }
      }

      if (!admin.isAdmin && !venueSchedule.isOpen) {
        return {
          type: 'disabled',
          text:
            venueSchedule.opensAt === 'never'
              ? t('checkout:weAreClosed')
              : `${t('checkout:weOpen')} ${formatRelative(venueSchedule.opensAt, new Date(), {
                  locale,
                })}`,
        }
      }

      if (currentOrderMode.value === 'delivery') {
        const leftToReachMinPrice = getMinOrderPriceIfNotReached(
          basket.prices,
        )?.leftToReachThreshold

        if (leftToReachMinPrice) {
          return {
            type: 'disabled',
            text: `${t('checkout:orderAnother')} ${price.format(leftToReachMinPrice)}`,
          }
        }

        return {
          type: 'submit',
          onSubmit: () => {
            return router.push(`/venue/${venue.name}/checkout/delivery`)
          },
          text: t('checkout:provideDeliveryAddress'),
        }
      }

      if (isEqual(keys(paymentMethods), ['online']) && !venue.paymentGateway) {
        return {
          type: 'disabled',
          text: t('common:paymentNotConfigured'),
        }
      }

      switch (orderState.type) {
        case 'failed':
          return {
            type: 'error',
            text: t('common:errorTryAgainLater'),
          }
        case 'loading':
          return {
            type: 'loading',
          }
        case 'selectingPaymentMethod':
          return {
            type: 'onClick',
            text: t('common:cancel'),
            onClick: () => {
              events.paymentMethodSelector.close$.next()
            },
          }
        case 'pending': {
          if (paymentMethodsFiltered.online && keys(paymentMethodsFiltered).length === 1) {
            return {
              type: 'submit',
              text: t('common:payCardOnline'),
              onSubmit: () => {
                return checkPersonalDataConsent() && events.sendOrder$.next({ type: 'online' })
              },
            }
          }

          if (keys(paymentMethodsFiltered).length > 1) {
            return {
              type: 'submit',
              text: t('common:choosePaymentMethod'),
              onSubmit: () => {
                return checkPersonalDataConsent() && events.paymentMethodSelector.open$.next()
              },
            }
          }

          const theOnlyPaymentMethod = keys(paymentMethodsFiltered)[0]!

          if (currentOrderMode.value === 'takeaway') {
            return {
              type: 'submit',
              text: t('checkout:orderTakeaway'),
              onSubmit: () => {
                return (
                  checkPersonalDataConsent() &&
                  events.sendOrder$.next({ type: theOnlyPaymentMethod })
                )
              },
            }
          }

          return {
            type: 'submit',
            text: t('common:order'),
            onSubmit: () => {
              return (
                checkPersonalDataConsent() && events.sendOrder$.next({ type: theOnlyPaymentMethod })
              )
            },
          }
        }
      }
    },
    (state) => {
      if (basket.size === 0 && 'text' in state) {
        return {
          type: 'disabled',
          text: state.text,
        } as CheckoutActionState
      }

      return state
    },
  )()

  const form = useForm({ id: CHECKOUT_FORM_ID })
  const actionState$ = useEffect$([actionState])
  useSubscription(
    form.submit$.pipe(
      filter((form) => {
        return form.isValid
      }),
      withLatestFrom(actionState$),
    ),
    ([, [actionState]]) => {
      if (actionState.type === 'submit') {
        actionState.onSubmit()
      }
    },
  )

  const actions: CheckoutViewController['actions'] = {
    closePaymentMethodSelector: makeEmmiterFor(events.paymentMethodSelector.close$),
    selectPaymentMethod: makeEmmiterFor(events.sendOrder$),
    toggleDeleteMode: makeEmmiterFor(events.toggleDeleteMode$),
  }

  const inputs = {
    phone: usePhoneInput({
      required: true,
      label: t('common:phone'),
    }),
    tableNumber: useInput({
      id: 'onSite/tableNumber',
      storage: stubStorage,
      startingValue: '',
      type: 'text',
      required: true,
      disabled: false,
      label: useTableNumber?.inputLabel ?? t('common:tableNumber'),
    }),
    onSiteComment: useInput({
      id: 'onSite/comment',
      storage: localStorage,
      startingValue: '',
      type: 'text',
      required: false,
      disabled: false,
      label: t('checkout:note'),
      rows: 2,
    }),
    takeawayComment: useInput({
      id: 'takeaway/comment',
      storage: localStorage,
      startingValue: '',
      type: 'text',
      required: false,
      disabled: false,
      label: t('checkout:whenToCookNotes'),
      rows: 2,
    }),
    name: useInput({
      id: 'contacts/name',
      storage: localStorage,
      startingValue: '',
      type: 'text',
      required: false,
      disabled: false,
      label: t('common:name'),
    }),
    email: useInput({
      id: 'contacts/email',
      storage: localStorage,
      startingValue: '',
      type: 'text',
      required: false,
      disabled: false,
      label: t('common:email'),
      helperText: t('checkout:emailHelperText'),
    }),
  }

  return {
    orderState,
    productListMode,
    paymentMethodsFiltered,
    inputs,
    actionState,
    actions,
  }
}

export function getMinOrderPriceIfNotReached(basketPrices: BasketOrderPrices) {
  if (basketPrices === 'loading' || !basketPrices.delivery?.minOrderPriceToAllowDelivery) {
    return undefined
  }
  const { minOrderPriceToAllowDelivery } = basketPrices.delivery
  if (minOrderPriceToAllowDelivery.leftToReachThreshold <= 0) {
    return undefined
  }
  return minOrderPriceToAllowDelivery
}
